import { useEffect, type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Navigate, useLocation } from 'react-router-dom';

import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';

import { endCustomerSessionGlobal } from '../../../shared/session/customerAuthSync.js';

import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';

import { ApiError } from '../../../shared/api/ApiClient.js';

import { isMissingCredentialUnauthorized } from '../../../shared/session/fetchPlatformMe.js';

import { ServerUnavailableScreen } from '../../entitlements/customerEntitlementGuards.js';

import { useAccountMe } from '../accountApi.js';

import { isAccountDirectoryFullWidthPath } from '../AccountShell.js';

import { ScreenState } from '../../../shared/ui/ScreenState.js';

import {

  COMPLETE_VERIFICATION_PATH,

  evaluateIdentityPipeline,

} from '../../../navigation/identityPipeline.js';

import { useIdentityPipelineNavigation } from '../../../navigation/useIdentityPipelineNavigation.js';

import { AccountDirectoryHubSessionLoading } from './accountDirectoryHubSessionLoading.js';



function readErrorCodeMessage(error: unknown): {

  readonly code: string | undefined;

  readonly message: string | undefined;

} {

  let code: string | undefined;

  if (

    error !== null &&

    typeof error === 'object' &&

    'code' in error &&

    typeof (error as { code?: string }).code === 'string'

  ) {

    code = (error as { code: string }).code;

  }

  let message: string | undefined;

  if (error instanceof Error) {

    message = error.message;

  } else if (

    error !== null &&

    typeof error === 'object' &&

    'message' in error &&

    typeof (error as { message?: unknown }).message === 'string'

  ) {

    message = (error as { message: string }).message;

  }

  return { code, message };

}



function isTransientAccountMeFailure(error: unknown): boolean {

  const { code, message } = readErrorCodeMessage(error);

  // G11 — missing_cred after established refresh-then-retry is terminal.

  if (isMissingCredentialUnauthorized(code, message)) {

    return false;

  }

  if (!(error instanceof ApiError)) {

    return error instanceof Error;

  }

  if (error.code === 'NETWORK_ERROR' || error.code === 'ME_TRANSIENT' || error.status === 0) {

    return true;

  }

  return error.status >= 500;

}



function AccountSessionHydratingStatus(): JSX.Element {

  const { t } = useTranslation('customer');

  return (

    <p className="p-4 text-sm text-[var(--color-on-surface-muted)]" role="status">

      {t('account.sessionHydrating')}

    </p>

  );

}



/** One loading surface while `/me` validates — directory hubs match their page skeletons. */

function AccountSessionMeLoading({ pathname }: { readonly pathname: string }): JSX.Element {

  if (isAccountDirectoryFullWidthPath(pathname)) {

    return <AccountDirectoryHubSessionLoading pathname={pathname} />;

  }

  return <ScreenState variant="skeleton" testId="account-session-validating" />;

}



/**

 * Gate account UI until platform `/me` matches in-memory `customerId`.

 */

export function AccountSessionValidator({

  children,

}: {

  children: ReactNode;

}): ReactNode {

  useTenantCode();

  const location = useLocation();

  const { session, sessionHydrated } = useCustomerSession();

  const me = useAccountMe(session !== null, { freshGate: true });

  const { navigateRecovery } = useIdentityPipelineNavigation();

  const transientMeFailure = me.isError && isTransientAccountMeFailure(me.error);



  const pipelineDecision = evaluateIdentityPipeline({

    routeMatched: true,

    entitlementsLoading: false,

    entitlementsGranted: true,

    entitlementMode: 'inline-unavailable',

    sessionHydrated,

    sessionPresent: session !== null,

    accountMeReady: !transientMeFailure && !me.isLoading && (me.data !== undefined || me.isError),

    accountMeValid:

      !transientMeFailure &&

      !me.isError &&

      me.data !== undefined &&

      session !== null &&

      me.data.customerId === session.customerId,

    accountMeTransient: transientMeFailure,

    oidcLandingPending: false,

    mustCompleteVerification: me.data?.mustCompleteVerification === true,

    pathname: location.pathname,

  });



  useEffect(() => {

    if (session === null || me.data === undefined) {

      return;

    }

    if (me.data.customerId !== session.customerId) {

      void endCustomerSessionGlobal({

        broadcast: true,

        callApi: true,

        reason: 'revoked',

      });

    }

  }, [me.data, session]);



  useEffect(() => {

    if (session === null || !me.isError || transientMeFailure) {

      return;

    }

    const { code, message } = readErrorCodeMessage(me.error);

    if (isMissingCredentialUnauthorized(code, message)) {

      // Refresh already attempted in fetchPlatformAccountMe; hard-end only.

      void endCustomerSessionGlobal({

        broadcast: false,

        callApi: false,

        reason: 'revoked',

      });

    }

  }, [me.error, me.isError, session, transientMeFailure]);



  useEffect(() => {

    if (transientMeFailure) {

      return;

    }

    if (pipelineDecision.recoveryMode === 'recovery-session-expired') {

      navigateRecovery('recovery-session-expired');

    }

  }, [navigateRecovery, pipelineDecision.recoveryMode, transientMeFailure]);



  if (session === null) {

    return <AccountSessionHydratingStatus />;

  }



  if (me.isLoading || (!me.isError && me.data === undefined)) {

    return <AccountSessionMeLoading pathname={location.pathname} />;

  }



  if (transientMeFailure) {

    return (

      <ServerUnavailableScreen

        onRetry={() => {

          void me.refetch();

        }}

      />

    );

  }



  if (me.isError || me.data === undefined || me.data.customerId !== session.customerId) {

    return <AccountSessionHydratingStatus />;

  }



  if (pipelineDecision.redirectToCompleteVerification === true) {

    return <Navigate to={COMPLETE_VERIFICATION_PATH} replace />;

  }



  return children;

}


